import { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';

import {
    NSLabel, NSInputNumberStepper, NSSwitch, NSSelect,
} from 'bricks';
import NSProFormaInput from 'bricks/NSProFormaInput/NSProFormaInput';
import {
    FORM_ROW_CLASS_NAME,
    FORM_LABEL_CLASS_NAME,
    ProjectActualsDiscrepanciesEnum,
    PROJECT_ACTUALS_ICON_WITH_TOOLTIP_TEXT,
    RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_GENERAL_INFO_TEXT,
    DATA_CENTER_INFO_ICON_TEXT,
    PRO_FORMA_SIZE_POWER_CAPACITY_OPTIONS,
    DISABLED_ESTIMATED_UNITS_TOOLTIP,
    DISABLED_EFFICIENCY_RATIO_TOOLTIP,
    ESTIMATED_RACKS_TOOLTIP_TEXT,
    PowerCapacityNames,
    POWER_CAPACITY_TYPE,
    KILOWATTS_IN_MEGAWATTS,
} from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import { numeralFormatter } from 'ns_libs/formatter';
import { calculateNetRentableArea, doesRentableSFExceedBuiltGSF, getProFormaModelDisplayCards } from 'views/ProFormaTable/helpers';
import { IProFormaValues } from 'views/ProFormaTable/types';
import { ProFormaModelIds } from 'constants/proForma';
import { UnitTypeIds } from 'constants/unitTypes';
import { useGrossBuildableArea } from 'views/ProFormaWizard/BasicInformation/hooks/useGrossBuildableArea';
import { faInfoCircle, faMemoCircleCheck, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import { IOptionAnyValue } from 'bricks/types';
import assertNever from 'helpers/assertNever';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import ProFormaSizeParking from './ProFormaSizeParking/ProFormaSizeParking';
import StoryAreaBreakdown from './StoryAreaBreakdown/StoryAreaBreakdown';
import ProFormaSizeOptionalFields from './ProFormaSizeOptionalFields/ProFormaSizeOptionalFields';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';
import CalculateSizeCard from '../CalculateSizeCard/CalculateSizeCard';

const SIZE_LABELS = {
    [ProFormaModelIds.APARTMENT_WITH_RETAIL]: {
        builtGrossSf: 'Apartment built gross SF',
        builtGrossSfPlaceholder: 'Enter apartment built gross SF',
        efficiencyRatio: 'Building efficiency ratio',
        estimatedUnits: 'Number of apartment units',
    },
} as const;

const DEFAULT_SIZE_LABELS = {
    builtGrossSf: 'Built gross SF',
    builtGrossSfPlaceholder: 'Enter built gross SF',
    efficiencyRatio: 'Efficiency ratio',
    estimatedUnits: 'Estimated no. of units',
} as const;

export interface IProFormaSizeDetailsProps {
    values: IProFormaValues;
    handleInputChange: (name: string, value: any) => void
    actualsDiscrepancies?: string[];
    isSignUpMode?: boolean;
}

const ProFormaSizeDetails = ({
    values, handleInputChange, actualsDiscrepancies, isSignUpMode = false,
}: IProFormaSizeDetailsProps) => {
    const {
        handleCreateBuildableLot, handleDeleteBuildableLot, handleUpdateBuildableLot,
        buildableLots, handleUpdateProForma, proForma, incomeSummary, shouldUseProjectActuals,
        operatingInfo, getLocalStorageValueByKey, updateLocalStorageByKey,
    } = useProFormaSocketContext();

    const {
        lots, createLot, updateLot, deleteLot, totalGrossBuildableArea, totalFar, totalLandAreaSquareFootage, updateAllLots,
    } = useGrossBuildableArea(values?.buildableLots?.grossBuildableAreaLots || []);

    const localStoragePowerCapacityOption = getLocalStorageValueByKey(POWER_CAPACITY_TYPE) as PowerCapacityNames;

    const [floorAreaVaries, setFloorAreaVaries] = useState(proForma.isFloorPlateAreaVaried || false);
    const [storyAreas, setStoryAreas] = useState<(number | null)[]>([]);
    const [powerCapacityOption, setPowerCapacityOption] = useState<IOptionAnyValue>(PRO_FORMA_SIZE_POWER_CAPACITY_OPTIONS[0]);

    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma.proFormaModelId);
    const isApartmentWithRetail = proForma.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL;
    const sizeLabels = SIZE_LABELS[proForma.proFormaModelId as keyof typeof SIZE_LABELS] ?? DEFAULT_SIZE_LABELS;
    const totalUnits = incomeSummary.totalUnits || 0; // from C2

    const netRentableArea = calculateNetRentableArea(
        proForma.proFormaModelId,
        Number(proForma.builtGrossSquareFootage),
        Number(proForma.efficiencyRatio),
        incomeSummary,
    );

    const valuesWithLotsData = {
        ...values,
        ...buildableLots,
        landArea: buildableLots?.totalLandAreaAcres ?? values.landArea,
        landAreaUnitTypeId: buildableLots?.totalLandAreaAcres != null ? UnitTypeIds.ACRE : values.landAreaUnitTypeId,
    };

    /**
     * Disable size fields:
     * - Apartment: if there is more than one unit mix row
     * - Commercial (Retail/Office/Industrial): rent roll only (not allocation) if there is more than one income row
     *   - efficiency ratio
     * - DataCenter: rent roll only (not allocation) if there is more than one income row
     *   - efficiency ratio
     *   - estimated # of racks
     */

    const disableSizeFieldsAffectingIncomeSection = (() => {
        if (isSignUpMode) {
            return false;
        }

        const proFormaModel = proForma.proFormaModelId as ProFormaModelIds;
        switch (proFormaModel) {
            case ProFormaModelIds.APARTMENT:
            case ProFormaModelIds.APARTMENT_WITH_RETAIL:
                return (incomeSummary.unitMix?.length || 0) > 1;

            case ProFormaModelIds.RETAIL:
            case ProFormaModelIds.OFFICE:
            case ProFormaModelIds.INDUSTRIAL:
                switch (operatingInfo.incomeCalculationType) {
                    case 'estimate':
                        return false;
                    case 'tenants':
                        return (incomeSummary.incomeTenants?.length || 0) > 1;
                    default:
                        assertNever(operatingInfo.incomeCalculationType);
                }
                break;

            case ProFormaModelIds.DATA_CENTER:
                switch (operatingInfo.incomeCalculationType) {
                    case 'estimate':
                        return false;
                    case 'tenants':
                        return (incomeSummary.incomeTenantsDataCenter?.length || 0) > 1;
                    default:
                        assertNever(operatingInfo.incomeCalculationType);
                }
                break;

            default:
                assertNever(proFormaModel);
        }
    })();

    const shouldShowRSFWarning = doesRentableSFExceedBuiltGSF(
        proForma.proFormaModelId, values.builtGrossSquareFootage, incomeSummary, operatingInfo.incomeCalculationType,
    );

    const displayCards = getProFormaModelDisplayCards(
        proForma.proFormaModelId,
        valuesWithLotsData,
        totalUnits,
        netRentableArea,
        buildableLots,
    );

    const handleStoryAreaChange = (story: number, area: number | null) => {
        const newAreas = [...storyAreas];
        newAreas[story - 1] = area;
        setStoryAreas(newAreas);
        // Ensure array is in order with 0s for empty positions and only contains numbers
        const orderedAreas: number[] = Array(Math.round(values.averageStories)).fill(0).map((_, index) => {
            const value = newAreas[index];
            return value === null || value === undefined ? 0 : Number(value);
        });
        handleUpdateProForma([{
            key: 'storyArea',
            value: orderedAreas,
        }]);
    };

    const handleSelectPowerCapacityOption = (pcType: IOptionAnyValue) => {
        updateLocalStorageByKey(POWER_CAPACITY_TYPE, pcType.value);
    };

    const handleUpdatePowerCapacityValue = (value: string | number) => {
        const convertedValue = powerCapacityOption.value === PowerCapacityNames.kW
            ? value
            : Number(value) * KILOWATTS_IN_MEGAWATTS;
        handleInputChange('powerCapacityKw', convertedValue);
        handleUpdateProForma([{ key: 'powerCapacityKw', value: String(convertedValue) }]);
    };

    useEffect(() => {
        if (isSignUpMode && !lots.length && values?.buildableLots?.grossBuildableAreaLots) {
            updateAllLots(values?.buildableLots?.grossBuildableAreaLots);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSignUpMode, values?.buildableLots?.grossBuildableAreaLots]);

    useEffect(() => {
        if (proForma.isFloorPlateAreaVaried !== null) {
            setFloorAreaVaries(proForma.isFloorPlateAreaVaried);
        }
    }, [proForma?.isFloorPlateAreaVaried]);

    useEffect(() => {
        if (proForma?.storyArea?.length) {
            setStoryAreas(proForma.storyArea.map(area => area ?? null));
        } else if (values.averageStories && !Number.isNaN(values.averageStories)) {
            setStoryAreas(Array(Math.round(values.averageStories)).fill(null));
        }
    }, [proForma?.storyArea, values.averageStories]);

    // Sync lots data to signUp values if changed
    useEffect(() => {
        if (isSignUpMode && lots !== values?.buildableLots?.grossBuildableAreaLots) {
            handleInputChange('buildableLots', {
                grossBuildableAreaLots: lots, totalGrossBuildableArea, totalFar, totalLandAreaSquareFootage,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSignUpMode, values, lots, totalGrossBuildableArea, totalFar, totalLandAreaSquareFootage]);

    useEffect(() => {
        if (localStoragePowerCapacityOption) {
            setPowerCapacityOption({ value: localStoragePowerCapacityOption, label: localStoragePowerCapacityOption });
        }
    }, [localStoragePowerCapacityOption]);

    const sfPerUnit = values.estimatedUnits && values.estimatedUnits > 0 && netRentableArea
        ? numeralFormatter(netRentableArea / values.estimatedUnits, '0,0') : '0';

    const sfPerRack = values.estimatedRacks && values.estimatedRacks > 0 && netRentableArea
        ? numeralFormatter(netRentableArea / values.estimatedRacks, '0,0') : '0';

    const sfPerFloor = values.averageStories && values.averageStories > 0 && values.builtGrossSquareFootage
        ? numeralFormatter(values.builtGrossSquareFootage / values.averageStories, '0,0') : '0';

    return (
        <Row>
            <Col md={12} lg={8}>
                <NSLabel className={FORM_LABEL_CLASS_NAME}>Gross buildable area</NSLabel>
                <CalculateSizeCard
                    onCreateLot={isSignUpMode ? createLot : handleCreateBuildableLot!}
                    onDeleteLot={isSignUpMode ? deleteLot : handleDeleteBuildableLot!}
                    onUpdateLot={isSignUpMode ? updateLot : handleUpdateBuildableLot!}
                    lots={isSignUpMode ? lots : buildableLots.grossBuildableAreaLots}
                    totalGrossBuildableArea={isSignUpMode ? totalGrossBuildableArea : buildableLots.totalGrossBuildableArea}
                />

                <div className={FORM_ROW_CLASS_NAME}>
                    <div className="d-flex col-4 pl-0">
                        <RenderIf isTrue={shouldUseProjectActuals
                            && actualsDiscrepancies?.includes(ProjectActualsDiscrepanciesEnum.BUILT_GROSS_SQUARE_FOOTAGE)}
                        >
                            <IconWithTooltip
                                name={ProjectActualsDiscrepanciesEnum.BUILT_GROSS_SQUARE_FOOTAGE}
                                icon={faMemoCircleCheck}
                                tooltipText={PROJECT_ACTUALS_ICON_WITH_TOOLTIP_TEXT}
                                className="my-auto mr-1"
                            />
                        </RenderIf>

                        <RenderIf isTrue={shouldShowRSFWarning}>
                            <IconWithTooltip
                                name={`RSFDiscrepancyIcon-${proForma.proFormaModelId}`}
                                icon={faTriangleExclamation}
                                tooltipText={RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_GENERAL_INFO_TEXT}
                                iconClassName="text-warning"
                                className="my-auto mr-1"
                            />
                        </RenderIf>

                        <NSLabel className={FORM_LABEL_CLASS_NAME}>{sizeLabels.builtGrossSf}</NSLabel>
                    </div>

                    <NSProFormaInput
                        type="number"
                        name="builtGrossSquareFootage"
                        value={values.builtGrossSquareFootage}
                        handleUpdateValue={value => {
                            handleInputChange('builtGrossSquareFootage', value);
                            handleUpdateProForma([
                                { key: 'builtGrossSquareFootage', value: String(value) },
                                { key: 'isBuiltGrossSfOverriddenByUser', value: true },
                            ]);
                        }}
                        placeholder={sizeLabels.builtGrossSfPlaceholder}
                        inputGroupClassName="p-0"
                        appendInputAddon={<div className="px-2">ft²</div>}
                    />
                </div>

                <RenderIf isTrue={isApartment}>
                    <div className={FORM_ROW_CLASS_NAME}>
                        <div className="d-flex col-4 pl-0 align-items-start">
                            <NSLabel className={`${FORM_LABEL_CLASS_NAME} pb-4`}>{sizeLabels.estimatedUnits}</NSLabel>
                        </div>

                        <div className="d-flex flex-column w-100">
                            <NSProFormaInput
                                key={`estimatedUnits-${proForma.estimatedUnits}`}
                                type="number"
                                name="estimatedUnits"
                                value={values.estimatedUnits}
                                handleUpdateValue={value => {
                                    handleInputChange('estimatedUnits', value);
                                    handleUpdateProForma([{ key: 'estimatedUnits', value: String(value) }]);
                                }}
                                placeholder="Enter estimated units"
                                inputGroupClassName="p-0"
                                disabled={disableSizeFieldsAffectingIncomeSection}
                                disabledTooltip={DISABLED_ESTIMATED_UNITS_TOOLTIP}
                            />
                            <div className="text-muted mt-1">
                                SF per unit:
                                {' '}
                                <span className="text-dark">{sfPerUnit}</span>
                            </div>
                        </div>
                    </div>
                </RenderIf>

                <RenderIf isTrue={isApartmentWithRetail}>
                    <div className={FORM_ROW_CLASS_NAME}>
                        <div className="d-flex col-4 pl-0">
                            <NSLabel className={FORM_LABEL_CLASS_NAME}>Retail built gross SF</NSLabel>
                        </div>

                        <NSProFormaInput
                            type="number"
                            name="retailBuiltGrossSquareFootage"
                            value={values.retailBuiltGrossSquareFootage}
                            handleUpdateValue={value => {
                                handleInputChange('retailBuiltGrossSquareFootage', value);
                                handleUpdateProForma([
                                    { key: 'retailBuiltGrossSquareFootage', value: String(value) },
                                ]);
                            }}
                            placeholder="Enter retail built gross SF"
                            inputGroupClassName="p-0"
                            appendInputAddon={<div className="px-2">ft²</div>}
                        />
                    </div>
                </RenderIf>

                <div className={FORM_ROW_CLASS_NAME}>
                    <div className="d-flex col-4 pl-0 align-items-start">
                        <NSLabel className={FORM_LABEL_CLASS_NAME}>{sizeLabels.efficiencyRatio}</NSLabel>
                    </div>

                    <div className="d-flex flex-column w-100">
                        <NSProFormaInput
                            key={`efficiencyRatio-${proForma.efficiencyRatio}`}
                            type="number"
                            name="efficiencyRatio"
                            value={values.efficiencyRatio?.toFixed(2)}
                            handleUpdateValue={value => {
                                handleInputChange('efficiencyRatio', Number(value) <= 100 ? value : 100);
                                handleUpdateProForma([
                                    {
                                        key: 'efficiencyRatio',
                                        value: ((Number(value) <= 100 ? Number(value) : 100) / 100).toFixed(4),
                                    }]);
                            }}
                            placeholder="Enter efficiency ratio"
                            inputGroupClassName="p-0"
                            appendInputAddon={<div className="px-2">%</div>}
                            disabled={disableSizeFieldsAffectingIncomeSection}
                            disabledTooltip={
                                disableSizeFieldsAffectingIncomeSection ? DISABLED_EFFICIENCY_RATIO_TOOLTIP : undefined
                            }
                            step={0.1}
                            max={100}
                            min={0}
                        />
                    </div>
                </div>

                <RenderIf isTrue={proForma.proFormaModelId === ProFormaModelIds.DATA_CENTER}>
                    <div className={FORM_ROW_CLASS_NAME}>
                        <div className="d-flex col-4 pl-0 align-items-start">
                            <NSLabel className={`${FORM_LABEL_CLASS_NAME} pb-4`}>Estimated no. of racks</NSLabel>
                        </div>

                        <div id="dataCenterRacksInput" className="d-flex flex-column w-100">
                            <NSProFormaInput
                                key={`estimatedRacks-${proForma.estimatedRacks}`}
                                type="number"
                                name="estimatedRacks"
                                value={values.estimatedRacks}
                                handleUpdateValue={value => {
                                    handleInputChange('estimatedRacks', value);
                                    handleUpdateProForma([{ key: 'estimatedRacks', value: String(value) }]);
                                }}
                                placeholder="Enter estimated racks"
                                inputGroupClassName="p-0"
                                disabled={disableSizeFieldsAffectingIncomeSection}
                                disabledTooltip={disableSizeFieldsAffectingIncomeSection ? ESTIMATED_RACKS_TOOLTIP_TEXT : undefined}
                            />

                            <div className="d-flex">
                                <div className="text-muted mt-1">
                                    SF per rack:
                                </div>
                                <div className="d-flex text-dark mt-1 ml-1">
                                    {sfPerRack}
                                    <IconWithTooltip
                                        name="dataCenterRacksInfoIcon"
                                        icon={faInfoCircle}
                                        tooltipText={DATA_CENTER_INFO_ICON_TEXT}
                                        className="my-auto ml-1"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={FORM_ROW_CLASS_NAME}>
                        <div className="d-flex col-4 pl-0 align-items-start">
                            <NSLabel className={FORM_LABEL_CLASS_NAME}>Power capacity</NSLabel>
                        </div>

                        <div className="d-flex flex-column w-100">
                            <NSProFormaInput
                                key={`powerCapacityKw-${powerCapacityOption.value}-${proForma.powerCapacityKw}`}
                                type="number"
                                name="powerCapacityKw"
                                customClassName="NSInput__appended-child--with-select"
                                value={powerCapacityOption.value === PowerCapacityNames.kW
                                    ? values.powerCapacityKw
                                    : Number(values.powerCapacityKw) / KILOWATTS_IN_MEGAWATTS}
                                handleUpdateValue={value => handleUpdatePowerCapacityValue(value)}
                                placeholder="Enter power capacity"
                                inputGroupClassName="p-0"
                                appendInputAddon={(
                                    <NSSelect
                                        name="powerCapacityOption"
                                        options={PRO_FORMA_SIZE_POWER_CAPACITY_OPTIONS}
                                        value={powerCapacityOption || PRO_FORMA_SIZE_POWER_CAPACITY_OPTIONS[0]}
                                        onChange={option => handleSelectPowerCapacityOption(option)}
                                        isClearable={false}
                                        isSearchable={false}
                                        styles={{
                                            ...customStyles,
                                            control: styles => ({
                                                ...styles,
                                                border: 'none',
                                                backgroundColor: '#47515d',
                                                width: '80px',
                                            }),
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </RenderIf>

                <RenderIf isTrue={!isApartment}>
                    <div className={FORM_ROW_CLASS_NAME}>
                        <NSLabel className={FORM_LABEL_CLASS_NAME}>Buildings</NSLabel>
                        <NSInputNumberStepper
                            name="buildings"
                            value={values.buildings}
                            onChange={(count: number) => {
                                handleUpdateProForma([{
                                    key: 'buildings',
                                    value: String(count),
                                }]);
                                handleInputChange('buildings', count);
                            }}
                            min={1}
                            inputGroupClassName="w-25"
                            step={1}
                        />
                    </div>
                    <div className={FORM_ROW_CLASS_NAME}>
                        <NSLabel className="pl-0 col-4 font-weight-normal text-dark text-nowrap">
                            {values.buildings <= 1 ? 'Stories' : 'Avg. # of stories'}
                        </NSLabel>
                        <div className={floorAreaVaries ? 'd-flex flex-column w-100 p-2 NSCard--level-3' : 'd-flex flex-column w-25'}>
                            <NSInputNumberStepper
                                name="averageStories"
                                value={values.averageStories}
                                onChange={(count: number) => {
                                    handleUpdateProForma([{
                                        key: 'averageStories',
                                        value: String(count),
                                    }]);
                                    handleInputChange('stories', count);
                                }}
                                min={1}
                                inputGroupClassName={floorAreaVaries ? 'w-50' : ''}
                                step={1}
                            />
                            <RenderIf isTrue={!floorAreaVaries && !isApartment && values.buildings === 1
                                && Math.round(values.averageStories) > 0}
                            >
                                <div className="text-muted mt-1">
                                    SF per floor:
                                    {' '}
                                    <span className="text-dark">{sfPerFloor}</span>
                                </div>
                            </RenderIf>
                            <RenderIf isTrue={!isApartment && values.buildings === 1
                                && Math.round(values.averageStories) > 1}
                            >
                                <div className="d-flex pt-1 pb-2">
                                    <NSSwitch
                                        name="floorAreaVaries"
                                        id="1"
                                        checked={floorAreaVaries}
                                        containerClassName="pr-1"
                                        onChange={() => {
                                            const newFloorAreaVaries = !floorAreaVaries;
                                            setFloorAreaVaries(newFloorAreaVaries);
                                            handleUpdateProForma([{ key: 'isFloorPlateAreaVaried', value: newFloorAreaVaries }]);
                                        }}
                                    />
                                    <NSLabel className={FORM_LABEL_CLASS_NAME}>
                                        Floor plate area varies
                                    </NSLabel>
                                </div>
                                <StoryAreaBreakdown
                                    storyAreas={storyAreas}
                                    floorAreaVaries={floorAreaVaries}
                                    averageStories={Math.round(values.averageStories)}
                                    onAreaChange={handleStoryAreaChange}
                                />
                            </RenderIf>
                        </div>
                    </div>
                </RenderIf>

                <RenderIf isTrue={proForma.proFormaModelId === ProFormaModelIds.INDUSTRIAL}>
                    <div className={FORM_ROW_CLASS_NAME}>
                        <NSLabel className={FORM_LABEL_CLASS_NAME}>Avg. clear height</NSLabel>
                        <NSProFormaInput
                            type="number"
                            name="averageClearHeight"
                            value={values.averageClearHeight}
                            handleUpdateValue={value => {
                                handleInputChange('averageClearHeight', value);
                                handleUpdateProForma([{ key: 'averageClearHeight', value: String(value) }]);
                            }}
                            placeholder="Enter avg. clear height"
                            inputGroupClassName="p-0"
                            appendInputAddon={<div className="px-2">ft</div>}
                        />
                    </div>
                </RenderIf>

                <ProFormaSizeParking
                    values={values}
                    handleInputChange={handleInputChange}
                />

                <RenderIf isTrue={isApartment}>
                    <ProFormaSizeOptionalFields
                        values={values}
                        handleInputChange={handleInputChange}
                        floorAreaVaries={floorAreaVaries}
                        setFloorAreaVaries={setFloorAreaVaries}
                        storyAreas={storyAreas}
                        handleStoryAreaChange={handleStoryAreaChange}
                        useSocketContext
                    />
                </RenderIf>

            </Col>
            <Col md={12} lg={4}>
                <Row className="mx-n1">
                    {displayCards.map(({
                        label, value, format, valueSuffix, fullWidth,
                    }) => (
                        <Col className="px-1 mb-2" md={12} lg={fullWidth ? 12 : 6} key={label}>
                            <MetricOutputCard
                                label={label}
                                value={`${numeralFormatter(value, format)} ${valueSuffix || ''}`}
                                showInProgress={!value}
                            />
                        </Col>
                    ))}
                </Row>
            </Col>
        </Row>
    );
};

export default ProFormaSizeDetails;
